import { InlineTool, SanitizerConfig } from '@editorjs/editorjs';

export default class TextColorTool implements InlineTool {
  static isInline = true;
  static title = 'Text Color';

  static get sanitize(): SanitizerConfig {
    return {
      span: {
        style: true
      }
    } as SanitizerConfig;
  }

  private button: HTMLButtonElement | null = null;
  private colorPicker: HTMLInputElement | null = null;
  private resetButton: HTMLButtonElement | null = null;
  private actionsContainer: HTMLDivElement | null = null;
  private readonly CSS = {
    button: 'ce-inline-tool',
    buttonActive: 'ce-inline-tool--active',
  };

  render(): HTMLElement {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add(this.CSS.button);
    this.button.innerHTML = `<svg width="20" height="18" viewBox="0 0 20 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.458 12.04l2.919 1.686-.781 1.417-.984-.03-.974 1.687H8.674l1.49-2.583-.508-.775.802-1.401zm.546-.952l3.624-6.327a1.597 1.597 0 0 1 2.182-.59 1.632 1.632 0 0 1 .615 2.201l-3.519 6.391-2.902-1.675zm-7.73 3.467h3.465a1.123 1.123 0 1 1 0 2.247H3.273a1.123 1.123 0 1 1 0-2.247z" fill="currentColor"/>
    </svg>`;

    return this.button;
  }

  renderActions(): HTMLElement {
    this.actionsContainer = document.createElement('div');
    this.actionsContainer.style.display = 'flex';
    this.actionsContainer.style.alignItems = 'center';
    this.actionsContainer.style.gap = '8px';

    this.colorPicker = document.createElement('input');
    this.colorPicker.type = 'color';
    this.colorPicker.value = '#000000';

    // Set up the color change handler once
    this.colorPicker.oninput = () => {
      this.applyColorToSelection();
    };

    this.resetButton = document.createElement('button');
    this.resetButton.type = 'button';
    this.resetButton.innerHTML = '✕';
    this.resetButton.title = 'Remove color';
    this.resetButton.style.padding = '4px 8px';
    this.resetButton.style.border = '1px solid #ccc';
    this.resetButton.style.borderRadius = '3px';
    this.resetButton.style.background = '#fff';
    this.resetButton.style.cursor = 'pointer';

    this.resetButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.removeColorFromSelection();
    };

    this.actionsContainer.appendChild(this.colorPicker);
    this.actionsContainer.appendChild(this.resetButton);

    return this.actionsContainer;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  surround(_range: Range): void {
    // For tools with action panels, surround() should not modify the DOM immediately.
    // The actual color application happens via the color picker's input handler.
    // This keeps the selection intact and allows the actions panel to stay open.
    return;
  }

  checkState(): boolean {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return false;
    }

    const range = selection.getRangeAt(0);
    const span = this.findColoredSpan(range.commonAncestorContainer);

    const isActive = !!span;

    if (this.button) {
      this.button.classList.toggle(this.CSS.buttonActive, isActive);
    }

    return isActive;
  }

  showActions(span: HTMLElement): void {
    if (!this.colorPicker || !this.resetButton) {
      return;
    }

    const currentColor = span.style.color;
    if (currentColor) {
      this.colorPicker.value = this.rgbToHex(currentColor);
    }

    // Reset button is always visible in actions
    this.resetButton.style.display = 'block';
  }

  hideActions(): void {
    if (this.resetButton) {
      this.resetButton.style.display = 'none';
    }
  }

  clear(): void {
    this.hideActions();
  }

  private applyColorToSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !this.colorPicker) {
      return;
    }

    const range = selection.getRangeAt(0);
    const existingSpan = this.findColoredSpan(range.commonAncestorContainer);

    if (existingSpan) {
      // Update existing span color
      existingSpan.style.color = this.colorPicker.value;
    } else {
      // Create new colored span
      const selectedText = range.extractContents();
      const span = document.createElement('span');
      span.style.color = this.colorPicker.value;
      span.appendChild(selectedText);
      range.insertNode(span);

      // Re-select the span content to keep toolbar open
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  }

  private removeColorFromSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const span = this.findColoredSpan(range.commonAncestorContainer);

    if (span && span.parentNode) {
      // Unwrap the span
      const parent = span.parentNode;
      while (span.firstChild) {
        parent.insertBefore(span.firstChild, span);
      }
      parent.removeChild(span);
    }
  }

  private findColoredSpan(node: Node): HTMLElement | null {
    if (!node) {
      return null;
    }

    // Check if current node is a span with color
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.tagName === 'SPAN' && element.style.color) {
        return element;
      }
    }

    // Check parent nodes
    if (node.parentElement) {
      return this.findColoredSpan(node.parentElement);
    }

    return null;
  }

  private rgbToHex(color: string): string {
    // If already hex, return as-is
    if (color.startsWith('#')) {
      return color;
    }

    // Parse RGB format: rgb(r, g, b)
    const rgb = color.match(/\d+/g);
    if (!rgb || rgb.length < 3) {
      return '#000000';
    }

    const r = parseInt(rgb[0]).toString(16).padStart(2, '0');
    const g = parseInt(rgb[1]).toString(16).padStart(2, '0');
    const b = parseInt(rgb[2]).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
  }
}
